import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import { X, Trash2, ShoppingBag, ShieldCheck, Tag, ArrowRight, Truck, Check } from 'lucide-react';
import { formatINR } from '../data/products';
import { resolveProductImage, handleProductImageError } from '../utils/imageHelper';

export default function CartDrawer() {
  const {
    cart,
    cartCount,
    cartSubtotal,
    discountAmount,
    gstAmount,
    cartTotal,
    shippingFee = 0,
    freeShippingThreshold = 50000,
    removeFromCart,
    updateQuantity,
    isCartOpen,
    setIsCartOpen,
    setIsCheckoutOpen,
    couponCode,
    couponMessage,
    appliedDiscountPercent,
    applyCoupon,
    removeCoupon,
    masterMetalRates,
    calculateProductPrice
  } = useShop();

  const [inputCoupon, setInputCoupon] = useState('');
  const safeCart = Array.isArray(cart) ? cart : [];

  // Resolved dynamically from storeSettings in ShopContext
  const threshold = Number(freeShippingThreshold) > 0 ? Number(freeShippingThreshold) : 50000;
  const progressPercent = Math.min(100, Math.round((cartSubtotal / threshold) * 100));

  const handleApplyCoupon = (e) => {
    e.preventDefault();
    if (!inputCoupon.trim()) return;
    applyCoupon(inputCoupon);
    setInputCoupon('');
  };

  const handleCheckoutProceed = () => {
    setIsCartOpen(false);
    setIsCheckoutOpen(true);
  };

  return (
    <div 
      className={`drawer-backdrop ${isCartOpen ? 'active' : ''}`}
      onClick={() => setIsCartOpen(false)}
    >
      <div 
        className="drawer-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--border-gold)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#0c0c14'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <ShoppingBag size={20} color="var(--gold-400)" />
            <h3 style={{ fontSize: '1.15rem', color: '#fff', fontWeight: 700 }}>
              Shopping Bag ({cartCount})
            </h3>
          </div>
          <button
            className="btn-icon"
            onClick={() => setIsCartOpen(false)}
            style={{ width: '36px', height: '36px' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Free Insured Delivery Progress */}
        <div style={{
          padding: '0.8rem 1.5rem',
          backgroundColor: 'rgba(212, 175, 55, 0.08)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
          fontSize: '0.78rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#fff' }}>
              <Truck size={14} color="var(--gold-400)" />
              {cartSubtotal >= freeShippingThreshold ? (
                <strong style={{ color: '#34d399' }}>✓ Free Insured VIP Delivery Unlocked</strong>
              ) : (
                <span>Add <strong>{formatINR(freeShippingThreshold - cartSubtotal)}</strong> more for Free Insured VIP Transit</span>
              )}
            </span>
            <span style={{ color: 'var(--gold-400)', fontWeight: 600 }}>{progressPercent}%</span>
          </div>
          <div style={{
            height: '5px',
            borderRadius: '999px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'var(--gold-gradient)',
              transition: 'width 0.4s ease'
            }} />
          </div>
        </div>

        {/* Cart Item List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.25rem 1.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {safeCart.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem 1rem',
              color: 'var(--text-muted)'
            }}>
              <ShoppingBag size={48} color="var(--gold-400)" style={{ margin: '0 auto 1rem', opacity: 0.6 }} />
              <h4 style={{ color: '#fff', fontSize: '1.1rem', marginBottom: '0.4rem' }}>
                Your Royal Bag is empty
              </h4>
              <p style={{ fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                Discover our signature gold and diamond collections to fill your bag with timeless sparkle.
              </p>
              <button 
                className="btn btn-gold btn-sm"
                onClick={() => setIsCartOpen(false)}
              >
                Browse Collections
              </button>
            </div>
          ) : (
            safeCart.map((item, idx) => {
              const product = item?.product || item;
              if (!product) return null;
              const pricing = calculateProductPrice ? calculateProductPrice(product, masterMetalRates) : null;
              const unitPrice = pricing?.finalPrice || Number(product.price) || 0;
              const prodId = product.id || `cart-${idx}`;
              const rawImg = (Array.isArray(product.images) && product.images[0]) || product.image;
              const prodImg = resolveProductImage(rawImg, product);
              const prodName = product.name || 'Fine Jewellery';
              const qty = Number(item.quantity) || 1;

              return (
                <div 
                  key={prodId}
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    padding: '1rem',
                    backgroundColor: '#151522',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-subtle)'
                  }}
                >
                  <img
                    src={prodImg}
                    alt={prodName}
                    onError={(e) => handleProductImageError(e, product)}
                    style={{
                      width: '76px',
                      height: '76px',
                      borderRadius: 'var(--radius-sm)',
                      objectFit: 'cover',
                      flexShrink: 0,
                      background: '#12121c'
                    }}
                  />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '0.5rem'
                    }}>
                      <h4 style={{
                        fontSize: '0.88rem',
                        color: '#fff',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }} title={prodName}>
                        {prodName}
                      </h4>
                      <button
                        onClick={() => removeFromCart(prodId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          padding: '2px',
                          transition: 'var(--transition)'
                        }}
                        title="Remove item"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <div style={{
                      fontSize: '0.74rem',
                      color: 'var(--gold-400)',
                      margin: '0.2rem 0 0.5rem'
                    }}>
                      {product.metal || '22K Gold'} {product.purity ? `• ${product.purity}` : ''}
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: 'auto'
                    }}>
                      {/* Qty controls */}
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#0e0e16',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 'var(--radius-sm)'
                      }}>
                        <button
                          onClick={() => updateQuantity(prodId, qty - 1)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#fff',
                            padding: '0.2rem 0.55rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          -
                        </button>
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, minWidth: '18px', textAlign: 'center' }}>
                          {qty}
                        </span>
                        <button
                          onClick={() => updateQuantity(prodId, qty + 1)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#fff',
                            padding: '0.2rem 0.55rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          +
                        </button>
                      </div>

                      {/* Line total */}
                      <span style={{
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#fff',
                        fontFamily: 'var(--font-serif)'
                      }}>
                        {formatINR(unitPrice * qty)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Bottom Checkout & Billing Section */}
        {cart.length > 0 && (
          <div style={{
            padding: '1.25rem 1.5rem',
            borderTop: '1px solid var(--border-gold)',
            backgroundColor: '#0c0c14'
          }}>
            {/* Promo coupon field */}
            <form onSubmit={handleApplyCoupon} style={{ marginBottom: '1.1rem' }}>
              <div style={{
                display: 'flex',
                gap: '0.5rem'
              }}>
                <div style={{
                  position: 'relative',
                  flex: 1
                }}>
                  <Tag size={15} color="var(--gold-400)" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '10px',
                    transform: 'translateY(-50%)'
                  }} />
                  <input
                    type="text"
                    placeholder="Enter Coupon (e.g. ROYAL10)"
                    value={inputCoupon}
                    onChange={(e) => setInputCoupon(e.target.value)}
                    style={{
                      width: '100%',
                      background: '#151522',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '0.55rem 0.8rem 0.55rem 2.1rem',
                      color: '#fff',
                      fontSize: '0.82rem',
                      textTransform: 'uppercase'
                    }}
                  />
                </div>
                <button 
                  type="submit" 
                  className="btn btn-gold btn-sm"
                  style={{ fontSize: '0.78rem', padding: '0.5rem 0.9rem' }}
                >
                  Apply
                </button>
              </div>

              {/* Coupon message tag */}
              {couponCode && (
                <div style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.35rem 0.7rem',
                  background: 'rgba(16, 185, 129, 0.12)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.74rem',
                  color: '#34d399'
                }}>
                  <span>✓ <strong>{couponCode}</strong> applied ({appliedDiscountPercent}% OFF)</span>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#fb7185',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </form>

            {/* Price Calculations */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.45rem',
              fontSize: '0.82rem',
              color: 'var(--text-secondary)',
              marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Bag Subtotal</span>
                <span style={{ color: '#fff' }}>{formatINR(cartSubtotal)}</span>
              </div>

              {discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#34d399' }}>
                  <span>Royal Discount ({appliedDiscountPercent}%)</span>
                  <span>- {formatINR(discountAmount)}</span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Making Charges</span>
                <span style={{ color: '#34d399' }}>FREE (Promotional)</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Applicable GST (3%)</span>
                <span style={{ color: '#fff' }}>{formatINR(gstAmount)}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Insured VIP Transit</span>
                <span style={{ color: (shippingFee > 0 ? 'var(--gold-400)' : '#34d399'), fontWeight: 600 }}>
                  {shippingFee > 0 ? formatINR(shippingFee) : 'FREE'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '0.6rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#fff'
              }}>
                <span>Total Payable</span>
                <span style={{ color: 'var(--gold-400)', fontFamily: 'var(--font-serif)' }}>
                  {formatINR(cartTotal)}
                </span>
              </div>
            </div>

            {/* Checkout Action Button */}
            <button
              className="btn btn-gold"
              onClick={handleCheckoutProceed}
              style={{
                width: '100%',
                padding: '0.85rem',
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span>Proceed to Checkout</span>
              <ArrowRight size={17} />
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              color: 'var(--text-muted)',
              fontSize: '0.72rem',
              marginTop: '0.7rem'
            }}>
              <ShieldCheck size={14} color="var(--gold-400)" />
              <span>256-Bit Encrypted Secure Payment</span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
